import {
  useContext, useEffect, useRef, useMemo, useState
} from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import {
  triggerLifeCycleEvent, getProductIndex, onLifeCycleEvent, getProduct, getSelectableVariantsIds,
  getBundleComponentItemIds, shouldUpdateAccessories, objectWithDefaultValue, updateSelectableVariantRef,
  isAccessoriesLoading, getProductState, getBundleProductIds, getNewProductsData, shouldUpdateProductsRef,
  getItemQuantities, getSelectedActiveItemIds, applyBundleAdjustmentsCache
} from '../utils/product-bundle-utils';
import {
  getBundleAccessoriesFromBundleProduct,
  getItemIdsFromBundleAccessories,
  getItemIdsFromBundleComponents,
  getUpdatedAccessoriesProduct,
  getUpdatedProduct,
  getUpdatedProducts,
  isMissingProducts,
  separateProductsByType
} from '../utils/bundle-features-utils';
import { getFilterResponseFlag } from '../utils/product-unavailable-utils';
import { PRODUCTS_CHANGED, ACCESSORIES_CHANGED, ALERT_TYPES } from '../constants/index';
import { useCheckAvailability } from './useCheckAvailability';
import { useGetBundleConfigSwitch } from './useGetBundleConfigSwitch';
import { useGetDeliveryAndStoreZip } from './useGetDeliveryAndStoreZip';

export const useBundleProducts = (
  bundleId, storeId, listenToProductsChange = true, isCustomerIdentified = false) => {
  const [productIds, setProductIds] = useState(null);
  const [productsState, setProductsState] = useState(null);
  const [accessoriesState, setAccessoriesState] = useState(null);
  const productsRef = useRef([]);
  const selectedAccessoriesRef = useRef([]);
  const updateProductRef = useRef(null);
  const selectableVariantProductsRef = useRef(null);
  // Composition-keyed snapshot of each item's bundlePromotionalAdjustments (see correctedProducts).
  const bundleAdjustmentsCacheRef = useRef(new Map());
  const hasBundleBeenModifiedRef = useRef(false);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const { storeZip, deliveryZip } = useGetDeliveryAndStoreZip();
  const {
    configBundleProducts,
    configSelectableVariantProducts,
    configExperienceType,
    configBundleType,
    configBundleProductsDetails,
    enableAccessories,
    enableRemoveItemPromoImage
  } = useGetBundleConfigSwitch();

  const addAccessories = (accessoriesData) => {
    const output = {
      accessoriesData: objectWithDefaultValue(accessoriesData?.products, []),
      bundleProducts: productsRef?.current
    };
    triggerLifeCycleEvent(ACCESSORIES_CHANGED, output);
  };

  const swapBack = (productId, isVariantSwap) => {
    const i = getProductIndex(productId, productsRef.current);
    const product = productsRef.current[i];

    if (product?.swappedProduct) {
      const { swappedProduct, ...newSwappedProduct } = product;
      const newProduct = {
        ...swappedProduct,
        swappedProduct: newSwappedProduct,
        alertType: isVariantSwap ? ALERT_TYPES.SWAPPED_VARIANT : ALERT_TYPES.SWAPPED,
        ...(isVariantSwap && { isVariantSwap: true })
      };

      let newProducts = [...productsRef.current];
      newProducts[i] = newProduct;
      productsRef.current = newProducts;
      setProductsState(newProducts);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProducts });
    }
  };

  const swapProducts = (prevProductId, newProductId, isVariantSwap) => {
    const i = getProductIndex(prevProductId, productsRef.current);
    if (!isVariantSwap && productsRef.current[i]?.swappedProduct?.itemId === newProductId) {
      swapBack(prevProductId);
      return;
    }
    if (i !== -1) {
      updateProductRef.current = {
        index: i,
        product: productsRef.current[i],
        ...(isVariantSwap && { isVariantSwap: true }),
        alertType: isVariantSwap ? ALERT_TYPES.SWAPPED_VARIANT : ALERT_TYPES.SWAPPED
      };
      productsRef.current[i] = {
        itemId: newProductId,
        swappedProduct: updateProductRef.current.product
      };
      const allActiveIds = productsRef.current.filter((p) => !p.removed).map((p) => p.itemId);
      setProductIds(allActiveIds);
    }
  };

  const addProduct = (prevProductId, newProductId) => {
    const i = getProductIndex(prevProductId, productsRef.current);
    if (prevProductId === newProductId) {
      let updatedProduct = [...productsRef.current];
      updatedProduct[i] = {
        ...updatedProduct[i],
        removed: false,
        alertType: ALERT_TYPES.ADDED
      };
      productsRef.current = updatedProduct;
      setProductsState(updatedProduct);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: updatedProduct });
    } else {
      updateProductRef.current = {
        index: i,
        product: null,
        alertType: ALERT_TYPES.ADDED
      };
      productsRef.current[i] = { itemId: newProductId };
      const allActiveIds = productsRef.current.filter((p) => !p.removed).map((p) => p.itemId);
      setProductIds(allActiveIds);
    }
  };

  const dismissAlert = (productId) => {
    const i = getProductIndex(productId, productsRef.current);
    if (i !== -1) {
      let newProducts = [...productsRef.current];
      newProducts[i] = {
        ...newProducts[i],
        alertType: null
      };
      productsRef.current = newProducts;
      setProductsState(newProducts);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProducts });
    }
  };

  const removeProduct = (productId) => {
    const i = getProductIndex(productId, productsRef.current);
    if (i !== -1) {
      let newProducts = [...productsRef.current];
      const { swappedProduct, ...newProduct } = objectWithDefaultValue(newProducts[i], {});
      newProducts[i] = {
        ...newProduct,
        removed: true,
        alertType: ALERT_TYPES.REMOVED
      };
      productsRef.current = newProducts;
      setProductsState(newProducts);
      triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProducts });
    }
  };

  useEffect(() => {
    if (!listenToProductsChange) {
      return;
    }
    onLifeCycleEvent(PRODUCTS_CHANGED, ({ output }) => {
      const { products } = output || {};
      if (productsRef?.current !== products) {
        productsRef.current = products;
        setProductsState(products);
      }
    });
    onLifeCycleEvent(ACCESSORIES_CHANGED, ({ output }) => {
      const { accessoriesData } = objectWithDefaultValue(output, {});
      setSelectedAccessories(accessoriesData);
      selectedAccessoriesRef.current = accessoriesData;
    });
  }, [listenToProductsChange]);

  const { data: productData, variables, loading: bundleLoading } = useDataModel('product', {
    variables: {
      itemId: bundleId,
      storeId,
      zipCode: deliveryZip || storeZip,
      isBrandPricingPolicyCompliant: isCustomerIdentified
    },
    skip: !bundleId
  });

  const bundleProduct = productData?.product;
  const stableProductData = useMemo(() => productData, [productData?.product?.itemId]);

  // Page-load date: only show a settled response fetched for the delivery zip and current store.
  // The page also queries with the store zip, and a loading query still returns the previous response.
  const settledBundleDateRef = useRef(null);
  if (!bundleLoading) {
    settledBundleDateRef.current = {
      zipCode: variables?.zipCode,
      storeId: variables?.storeId,
      date: bundleProduct?.bundleSpecificationDetails?.deliveryDate || null
    };
  }
  const settledBundleDate = settledBundleDateRef.current;
  const bundleDeliveryDate = (deliveryZip && settledBundleDate?.zipCode === deliveryZip
    && settledBundleDate?.storeId === variables?.storeId)
    ? settledBundleDate.date
    : null;

  const bundleComponentItemIds = useMemo(
    () => getBundleComponentItemIds(stableProductData),
    [stableProductData]
  );

  const bundleItemIds = useMemo(
    () => getItemIdsFromBundleComponents(productIds, bundleComponentItemIds, configBundleProducts),
    [productIds, bundleComponentItemIds, configBundleProducts]
  );

  const selectableVariantsItemIds = useMemo(
    () => getSelectableVariantsIds({
      productData: stableProductData?.product,
      bundleItemIds,
      configSelectableVariantProducts
    }),
    [stableProductData, bundleItemIds, configSelectableVariantProducts]
  );

  const bundleAccessories = useMemo(
    () => getBundleAccessoriesFromBundleProduct(stableProductData?.product, enableAccessories),
    [stableProductData, enableAccessories]
  );

  const accessoryItemIds = useMemo(
    () => getItemIdsFromBundleAccessories(bundleAccessories),
    [bundleAccessories]
  );

  const bundleProductIds = useMemo(
    () => getBundleProductIds(productIds, bundleItemIds, selectableVariantsItemIds, accessoryItemIds),
    [productIds, bundleItemIds, selectableVariantsItemIds, accessoryItemIds]
  );

  const selectedAccessoryIds = useMemo(
    () => (selectedAccessories || [])
      .map((accessory) => accessory?.defaultProductId || accessory?.itemId)
      .filter(Boolean),
    [selectedAccessories]
  );

  // True once the user changes the bundle (swap/remove/add/fuel-type/accessory). Drives the full-bundle
  // re-fetch and disables the stale parent-rollup fallback. Latches: it must stay true even when a change
  // returns the bundle to a pristine shape (e.g. deselecting all accessories), or it would revert to the
  // cached superset with no re-fetch. Reads productsRef.current (synchronously updated by handlers).
  const isBundleModified = useMemo(() => {
    const current = productsRef.current || [];
    const modifiedNow = !!productIds?.length
      || selectedAccessoryIds.length > 0
      || current.some((p) => p?.removed || p?.alertType || p?.swappedProduct);
    if (modifiedNow) {
      hasBundleBeenModifiedRef.current = true;
    }
    return hasBundleBeenModifiedRef.current;
  }, [productIds, productsState, selectedAccessoryIds]);

  // Initial load fetches the full superset (variant options + accessory offerings) so tiles get display
  // data. On any change, fetch only the selected bundle so the backend recomputes each item's
  // bundlePromotionalAdjustments in the new context.
  const fetchItemIds = useMemo(() => {
    if (!isBundleModified || !productsRef.current?.length) {
      return bundleProductIds;
    }
    return getSelectedActiveItemIds(productsRef.current, productIds, selectedAccessoryIds);
  }, [bundleProductIds, productIds, productsState, selectedAccessoryIds, isBundleModified]);

  // Parallel to fetchItemIds: each id's quantity (accessories may be qty > 1) so the backend factors
  // quantity into the bundle price adjustment.
  const itemQuantities = useMemo(
    () => getItemQuantities(fetchItemIds, bundleProduct, configBundleProductsDetails, selectedAccessories),
    [fetchItemIds, bundleProduct, configBundleProductsDetails, selectedAccessories]
  );

  const filterResponse = getFilterResponseFlag(stableProductData);

  const skip = objectWithDefaultValue(!stableProductData?.product, !fetchItemIds.length);

  const { data, loading, error: productsError } = useDataModel('products', {
    variables: {
      itemIds: fetchItemIds,
      itemQuantities,
      isBrandPricingPolicyCompliant: isCustomerIdentified,
      filterResponse
    },
    skip
  });

  // Stable key identifying the current bundle composition, used to snapshot/restore the correct
  // bundle-context adjustments client-side (see applyBundleAdjustmentsCache for why this is needed).
  const compositionKey = useMemo(
    () => JSON.stringify([fetchItemIds, itemQuantities, filterResponse, isCustomerIdentified]),
    [fetchItemIds, itemQuantities, filterResponse, isCustomerIdentified]
  );

  // !loading: only snapshot a settled response — an in-flight data.products may still hold the previous composition.
  const correctedProducts = useMemo(
    () => applyBundleAdjustmentsCache(data?.products, bundleAdjustmentsCacheRef.current, compositionKey, !loading),
    [data?.products, compositionKey, loading]
  );

  const updatedProduct = useMemo(
    () => getUpdatedProduct(
      getProduct(bundleProduct, configExperienceType, configBundleType, configBundleProducts),
      objectWithDefaultValue(correctedProducts, []),
      configExperienceType,
      configBundleType
    ),
    [bundleProduct, correctedProducts, configExperienceType, configBundleType, configBundleProducts]
  );

  const activeItemIds = useMemo(() => {
    const currentProducts = productsState || productsRef.current;
    // On initial load, include all IDs (superset) for OOS status checks on variants
    if (!currentProducts?.length) {
      return bundleProductIds;
    }
    // After user interaction, use only the currently selected composition for accurate delivery date
    return getSelectedActiveItemIds(currentProducts, productIds, selectedAccessoryIds);
  }, [productsState, bundleProductIds, selectedAccessoryIds, productIds]);

  const { data: checkAvailabilityData, loading: checkAvailabilityLoading } = useCheckAvailability({
    product: updatedProduct,
    itemIds: activeItemIds
  });

  // The page-load check includes every variant and accessory, so ignore its date until the bundle changes.
  // After a change, wait for the new check: a loading query still returns the previous response.
  const deliveryDateLoading = isBundleModified && !!checkAvailabilityLoading;
  const checkAvailabilityDeliveryDate = useMemo(() => {
    if (!isBundleModified || checkAvailabilityLoading) return null;
    const services = checkAvailabilityData?.checkAvailability?.fulfillmentOptions?.[0]?.services;
    const directDelivery = services?.find((service) => service?.type === 'DirectDelivery');
    return directDelivery?.earliestAvailabilityDate || null;
  }, [checkAvailabilityData, isBundleModified, checkAvailabilityLoading]);

  const updatedBundleProducts = useMemo(() =>
    getUpdatedProducts(
      updatedProduct, objectWithDefaultValue(correctedProducts, []),
      bundleComponentItemIds, configExperienceType, configBundleType, configBundleProductsDetails,
      checkAvailabilityData, selectableVariantsItemIds, accessoryItemIds, enableRemoveItemPromoImage,
      deliveryZip, storeZip
    ),
  [
    updatedProduct, correctedProducts, bundleComponentItemIds, configExperienceType, configBundleType,
    configBundleProductsDetails, checkAvailabilityData, selectableVariantsItemIds, accessoryItemIds,
    enableRemoveItemPromoImage, deliveryZip, storeZip
  ]
  );

  const {
    updatedProducts,
    accessoryProducts,
    selectableVariants
  } = useMemo(() => separateProductsByType(updatedBundleProducts), [updatedBundleProducts]);

  selectableVariantProductsRef.current = updateSelectableVariantRef(selectableVariantProductsRef, selectableVariants,
    updatedProduct, checkAvailabilityData);

  useEffect(() => {
    productsRef.current = [];
  }, [isCustomerIdentified]);

  useEffect(() => {
    if (shouldUpdateProductsRef(data, updatedProduct, checkAvailabilityData)) {
      let newProductsData = getNewProductsData(productsRef, updatedProducts);
      if (updateProductRef?.current !== null) {
        const isIncomplete = isMissingProducts(bundleComponentItemIds, newProductsData, selectableVariantsItemIds);
        const isNewProduct = updateProductRef?.current.product === null;
        const product = objectWithDefaultValue((isNewProduct ? productsRef.current[updateProductRef?.current.index]
          : updateProductRef?.current?.product), {});

        // Find the swapped/added item in the full response by itemId
        const targetItemId = productsRef.current[updateProductRef.current.index]?.itemId;
        const swappedItemData = updatedProducts?.find((p) => p.itemId === targetItemId) || updatedProducts?.[0];

        const newProduct = {
          ...swappedItemData,
          swappedProduct: isNewProduct ? null : product,
          ...(updateProductRef?.current?.isVariantSwap && { isVariantSwap: true }),
          ...(updateProductRef?.current?.alertType && { alertType: updateProductRef.current.alertType }),
          isIncomplete,
          // Added products have only a bare {itemId} placeholder (no features),
          // so fall back to the freshly-computed config; swaps keep the prior features.
          features: product?.features ?? swappedItemData?.features,
          quantity: objectWithDefaultValue(product?.quantity, 1),
          priority: objectWithDefaultValue(product?.priority, 0)
        };
        newProductsData[updateProductRef?.current.index] = newProduct;

        // Update pricing for all other products with fresh data from the response
        newProductsData = newProductsData.map((existing, idx) => {
          if (idx === updateProductRef.current.index) return existing;
          const fresh = updatedProducts?.find((p) => p.itemId === existing?.itemId);
          if (fresh) {
            return { ...existing, pricing: fresh.pricing };
          }
          return existing;
        });

        triggerLifeCycleEvent(PRODUCTS_CHANGED, { products: newProductsData });
        updateProductRef.current = null;
      }
      productsRef.current = newProductsData;
    }
  }, [data, checkAvailabilityData]);

  useEffect(() => {
    const updateAccessories = shouldUpdateAccessories(accessoryProducts, updatedProduct,
      bundleAccessories, accessoriesState);
    if (updateAccessories) {
      const result = getUpdatedAccessoriesProduct(
        accessoryProducts,
        updatedProduct,
        bundleAccessories,
        configExperienceType,
        configBundleType
      );
      setAccessoriesState(result);
    }
  }, [accessoryProducts, updatedProduct, bundleAccessories, configExperienceType, configBundleType, accessoriesState]);

  const products = useMemo(() => {
    const base = getProductState(productsState, updatedProducts);
    if (!productsState) return base; // initial load: base is already the fresh response
    // Merge fresh pricing into retained items so bundle-context adjustments refresh on every change.
    // Removed items aren't in the response, so they keep their removed state.
    return base.map((item) => {
      const fresh = updatedProducts?.find((p) => p?.itemId === item?.itemId);
      return fresh ? { ...item, pricing: fresh.pricing } : item;
    });
  }, [productsState, updatedProducts]);

  // Accessories' base pricing comes from the selection payload, not the products query. Merge in fresh
  // bundle-context pricing from the response so their adjustments aren't stale. Derived, not state, to
  // avoid a re-fetch loop.
  const pricedSelectedAccessories = useMemo(() => {
    if (!selectedAccessories?.length) return selectedAccessories;
    const freshPricingById = new Map((correctedProducts || []).map((p) => [p?.itemId, p?.pricing]));
    return selectedAccessories.map((accessory) => {
      const id = accessory?.defaultProductId || accessory?.itemId;
      const freshPricing = freshPricingById.get(id);
      return freshPricing ? { ...accessory, pricing: freshPricing } : accessory;
    });
  }, [selectedAccessories, correctedProducts]);

  return {
    itemId: bundleId,
    product: updatedProduct,
    products,
    bundleDeliveryDate,
    checkAvailabilityDeliveryDate,
    deliveryDateLoading,
    selectableVariantProducts: selectableVariantProductsRef?.current,
    accessories: accessoriesState,
    selectedAccessories: pricedSelectedAccessories,
    isBundleModified,
    loadingAccessories: isAccessoriesLoading(loading, !accessoriesState, bundleAccessories?.length > 0),
    loading,
    bundleLoading,
    error: false,
    productsError,
    variables,
    bundleProductsCount: bundleComponentItemIds?.length,
    swapProducts,
    swapBack,
    addProduct,
    removeProduct,
    dismissAlert,
    addAccessories
  };
};
